/**
 * Address Lookup Service
 * Integrates with address lookup APIs to fetch addresses for UK postcodes
 * Supports: getAddress.io (for actual street addresses) and postcodes.io (for validation)
 */

export interface Address {
  line1: string
  line2?: string
  line3?: string
  town: string
  county: string
  postcode: string
  fullAddress: string // Formatted full address for display
}

export interface AddressLookupResult {
  success: boolean
  addresses: Address[]
  error?: string
}

/**
 * Lookup addresses using getAddress.io API
 * Note: Requires API key - set VITE_GETADDRESS_API_KEY in environment
 */
const lookupWithGetAddress = async (postcode: string): Promise<AddressLookupResult> => {
  const apiKey = import.meta.env.VITE_GETADDRESS_API_KEY

  if (!apiKey) {
    console.warn('getAddress.io API key not configured')
    return {
      success: false,
      addresses: [],
      error: 'Address lookup service not configured'
    }
  }

  try {
    const cleanPostcode = postcode.replace(/\s/g, '')
    const response = await fetch(
      `https://api.getAddress.io/find/${cleanPostcode}?api-key=${apiKey}&expand=true`
    )

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          addresses: [],
          error: 'No addresses found for this postcode'
        }
      }
      throw new Error(`API returned status ${response.status}`)
    }

    const data = await response.json()

    if (!data.addresses || data.addresses.length === 0) {
      return {
        success: false,
        addresses: [],
        error: 'No addresses found for this postcode'
      }
    }

    // Convert getAddress.io format to our Address format
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Define type for getAddress.io API response
    const addresses: Address[] = data.addresses.map((addr: any) => {
      const parts = [
        addr.line_1,
        addr.line_2,
        addr.line_3,
        addr.line_4,
        addr.town_or_city,
        addr.county
      ].filter(Boolean)

      return {
        line1: addr.line_1 || '',
        line2: addr.line_2 || '',
        line3: addr.line_3 || '',
        town: addr.town_or_city || '',
        county: addr.county || '',
        postcode: addr.postcode || postcode,
        fullAddress: parts.join(', ')
      }
    })

    return {
      success: true,
      addresses
    }
  } catch (error) {
    console.error('getAddress.io lookup error:', error)
    return {
      success: false,
      addresses: [],
      error: 'Failed to lookup addresses'
    }
  }
}

/**
 * Validate postcode using postcodes.io (free, no API key required)
 */
const validateWithPostcodesIo = async (postcode: string): Promise<boolean> => {
  try {
    const cleanPostcode = postcode.replace(/\s/g, '').toUpperCase()
    const response = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}/validate`)

    if (!response.ok) {
      return false
    }

    const data = await response.json()
    return data.result === true
  } catch (error) {
    console.error('Postcode validation error:', error)
    return false
  }
}

/**
 * Main address lookup function
 * Attempts to use getAddress.io, falls back to validation-only mode
 */
export const lookupAddresses = async (postcode: string): Promise<AddressLookupResult> => {
  // Validate postcode format first
  if (!isValidUKPostcode(postcode)) {
    return {
      success: false,
      addresses: [],
      error: 'Invalid postcode format'
    }
  }

  // Try getAddress.io for full address lookup
  const result = await lookupWithGetAddress(postcode)

  if (result.success) {
    return result
  }

  // If getAddress.io fails or not configured, validate with postcodes.io
  const isValid = await validateWithPostcodesIo(postcode)

  if (!isValid) {
    return {
      success: false,
      addresses: [],
      error: 'Postcode not found'
    }
  }

  // Postcode is valid but we don't have address data
  // Return error to trigger manual entry
  return {
    success: false,
    addresses: [],
    error: 'We\'re having trouble finding addresses for this postcode'
  }
}

/**
 * Validate UK postcode format (client-side validation)
 */
export const isValidUKPostcode = (postcode: string): boolean => {
  const cleaned = postcode.replace(/\s/g, '').toUpperCase()
  const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?[0-9][A-Z]{2}$/
  return postcodeRegex.test(cleaned)
}

/**
 * Format postcode with standard spacing (e.g., "SW1A1AA" -> "SW1A 1AA")
 */
export const formatPostcode = (postcode: string): string => {
  const cleaned = postcode.replace(/\s/g, '').toUpperCase()
  if (cleaned.length < 5) return cleaned

  // Insert space before last 3 characters
  return `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`
}
